
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mic, MicOff, Send, Volume1, Upload, Plus, History, Trash2, XCircle, Sparkles, Bot } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

type Message = {
  role: 'user' | 'assistant';
  content: string;
};

type ChatSession = {
  id: string;
  created_at: string;
  title: string;
  messages?: Message[];
};

export const LunaChat = () => {
  const { user } = useAuth();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  useEffect(() => {
    if (user) {
      fetchChatSessions();
    }
  }, [user]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  const fetchChatSessions = async () => {
    if (!user) return;
    
    try {
      console.log("Fetching chat sessions for user:", user.id);
      
      const { data: sessionData, error: sessionError } = await supabase
        .from('chat_sessions')
        .select('id, created_at, title')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (sessionError) {
        console.error("Error fetching chat sessions:", sessionError);
        if (sessionError.code === '42P01') {
          console.log("Chat sessions table does not exist, creating a new chat");
          createNewChat();
          return;
        } else {
          throw sessionError;
        }
      }
      
      console.log("Retrieved chat sessions:", sessionData?.length || 0);
      setChatSessions(sessionData || []);
      
      if (!sessionData || sessionData.length === 0) {
        console.log("No chat sessions found, creating a new one");
        createNewChat();
      } else {
        console.log("Loading most recent chat:", sessionData[0].id);
        setActiveChatId(sessionData[0].id);
        loadChatMessages(sessionData[0].id);
      }
    } catch (error) {
      console.error("Error in fetchChatSessions:", error);
      toast.error("Could not load your chat history. Creating a new chat.", {
        position: "bottom-right",
        dismissible: true,
      });
      createNewChat();
    }
  };

  const loadChatMessages = async (chatId: string) => {
    if (!user) return;
    
    try {
      console.log("Loading chat messages for chat:", chatId);
      
      const { data, error } = await supabase
        .from('chat_history')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error("Error loading chat messages:", error);
        throw error;
      }
      
      if (data && data.length > 0) {
        console.log("Retrieved chat messages:", data.length);
        const formattedHistory = data.map((item: any) => ({
          role: item.role as 'user' | 'assistant',
          content: item.content
        }));
        
        setMessages(formattedHistory);
      } else {
        console.log("No messages found for this chat");
        setMessages([]);
      }
    } catch (error) {
      console.error("Error in loadChatMessages:", error);
      toast.error("Could not load chat messages", {
        position: "bottom-right",
        dismissible: true,
      });
      setMessages([]);
    }
  };

  const createNewChat = async () => {
    if (!user) return;
    
    try {
      console.log("Creating new chat session for user:", user.id);
      
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          title: 'New Chat'
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating new chat session:", error);
        const tempId = Date.now().toString();
        setChatSessions(prev => [{ id: tempId, title: 'New Chat', created_at: new Date().toISOString() }, ...prev]);
        setActiveChatId(tempId);
        setMessages([]);
        return;
      }
      
      console.log("Created new chat session:", data.id);
      
      setChatSessions(prev => [data, ...prev]);
      setActiveChatId(data.id);
      setMessages([]);
    } catch (error) {
      console.error("Error in createNewChat:", error);
      toast.error("Failed to create new chat", {
        position: "bottom-right",
        dismissible: true,
      });
    }
  };

  const updateChatTitle = async (chatId: string, firstMessage: string) => {
    if (!user) return;
    
    const title = firstMessage.length > 30 
      ? `${firstMessage.substring(0, 30)}...` 
      : firstMessage;
    
    try {
      console.log("Updating chat title for chat:", chatId);
      
      await supabase
        .from('chat_sessions')
        .update({ title })
        .eq('id', chatId);
      
      console.log("Chat title updated to:", title);
      
      setChatSessions(prev => 
        prev.map(session => 
          session.id === chatId ? { ...session, title } : session
        )
      );
    } catch (error) {
      console.error("Error updating chat title:", error);
    }
  };

  const deleteChat = async (chatId: string) => {
    if (!user) return;
    
    try {
      console.log("Deleting chat session:", chatId);
      
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', chatId);
      
      if (error) {
        console.error("Error deleting chat session:", error);
        throw error;
      }
      
      console.log("Chat session deleted successfully");
      
      setChatSessions(prev => prev.filter(session => session.id !== chatId));
      
      if (chatId === activeChatId) {
        if (chatSessions.length > 1) {
          const newActiveChat = chatSessions.find(session => session.id !== chatId);
          if (newActiveChat) {
            console.log("Switching to another chat:", newActiveChat.id);
            setActiveChatId(newActiveChat.id);
            loadChatMessages(newActiveChat.id);
          } else {
            console.log("No other chats found, creating a new one");
            createNewChat();
          }
        } else {
          console.log("No other chats found, creating a new one");
          createNewChat();
        }
      }
      
      toast.success("Chat deleted successfully", {
        position: "bottom-right",
        dismissible: true,
      });
    } catch (error) {
      console.error("Error in deleteChat:", error);
      toast.error("Failed to delete chat", {
        position: "bottom-right",
        dismissible: true,
      });
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !user || !activeChatId) return;
    
    const userMessage: Message = { role: 'user', content: input };
    
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    
    try {
      console.log("Sending message in chat:", activeChatId);
      
      const { error: insertError } = await supabase.from('chat_history').insert({
        user_id: user.id,
        chat_id: activeChatId,
        role: 'user',
        content: input,
      });

      if (insertError) {
        console.error("Error storing user message:", insertError);
        throw insertError;
      }
      
      if (messages.length === 0) {
        updateChatTitle(activeChatId, input);
      }
      
      const apiMessages = [...messages, userMessage]
        .map(({ role, content }) => ({ 
          role, 
          content,
          chat_id: activeChatId
        }));
      
      console.log("Calling Luna Chat API");
      
      const response = await supabase.functions.invoke('luna-chat', {
        body: { 
          messages: apiMessages,
          userId: user.id,
          chatId: activeChatId
        },
      });
      
      if (response.error) {
        console.error("Error from Luna Chat API:", response.error);
        throw new Error(response.error.message || 'Failed to get response from Luna');
      }
      
      const data = response.data;
      console.log("Received response from Luna Chat API:", data);
      
      const lunaReply: Message = data.reply;
      
      const { error: replyError } = await supabase.from('chat_history').insert({
        user_id: user.id,
        chat_id: activeChatId,
        role: 'assistant',
        content: lunaReply.content,
      });

      if (replyError) {
        console.error("Error storing assistant message:", replyError);
      }
      
      setMessages(prev => [...prev, lunaReply]);
      
    } catch (error) {
      console.error("Error in handleSendMessage:", error);
      toast.error("Something went wrong. Please try again later.", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await convertSpeechToText(audioBlob);
        
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("Could not access microphone", {
        position: "bottom-right",
        dismissible: true,
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const convertSpeechToText = async (audioBlob: Blob) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result?.toString().split(',')[1];
        
        if (!base64Audio) return;
        
        setIsLoading(true);
        
        const { data, error } = await supabase.functions.invoke('speech-to-text', {
          body: { audio: base64Audio },
        });
        
        if (error) {
          throw new Error(error.message || 'Failed to convert speech to text');
        }
        
        if (data?.text) {
          setInput(data.text);
        }
        
        setIsLoading(false);
      };
    } catch (error) {
      console.error("Error converting speech to text:", error);
      toast.error("Failed to process speech", {
        position: "bottom-right",
        dismissible: true,
      });
      setIsLoading(false);
    }
  };

  const playTextToSpeech = async (text: string) => {
    try {
      setIsPlaying(true);
      
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { text, voice: 'nova' },
      });
      
      if (error) {
        throw new Error(error.message || 'Failed to convert text to speech');
      }
      
      if (data?.audioContent) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audioContent}`);
        audio.onended = () => setIsPlaying(false);
        await audio.play();
      } else {
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("Error playing text to speech:", error);
      toast.error("Failed to play audio", {
        position: "bottom-right",
        dismissible: true,
      });
      setIsPlaying(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Please upload a file smaller than 5MB", {
        position: "bottom-right",
        dismissible: true,
      });
      return;
    }
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const fileContent = event.target?.result as string;
        
        const fileMessage = `I've uploaded a file named "${file.name}" (${(file.size / 1024).toFixed(2)} KB). Can you help me with this?`;
        setInput(fileMessage);
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Failed to read file", {
        position: "bottom-right",
        dismissible: true,
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="flex h-[calc(100vh-180px)] relative overflow-hidden rounded-xl shadow-xl">
      <AnimatePresence>
        {showSidebar && (
          <motion.div 
            className="w-72 bg-white rounded-l-xl shadow-md flex flex-col overflow-hidden border-r border-gray-100"
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="p-4 bg-gradient-to-r from-expitrack-primary/5 to-expitrack-primary/10 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">Chat History</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-gray-500 hover:text-expitrack-primary hover:bg-expitrack-primary/10 rounded-full"
                  onClick={() => setShowSidebar(false)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
              <Button 
                onClick={createNewChat}
                className="w-full bg-expitrack-primary hover:bg-expitrack-primary/90 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <Plus className="mr-2 h-4 w-4" /> New Chat
              </Button>
            </div>
            
            <ScrollArea className="flex-grow">
              <div className="p-2 space-y-1">
                <AnimatePresence>
                  {chatSessions.map((session, index) => (
                    <motion.div 
                      key={session.id}
                      className={`p-3 rounded-lg cursor-pointer flex justify-between items-center group ${
                        activeChatId === session.id 
                          ? 'bg-gradient-to-r from-expitrack-primary/20 to-expitrack-primary/10 text-expitrack-primary' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setActiveChatId(session.id);
                        loadChatMessages(session.id);
                      }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ scale: 1.02, boxShadow: "0px 3px 8px rgba(0,0,0,0.05)" }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="truncate flex-1">
                        <div className="font-medium truncate">{session.title}</div>
                        <div className="text-xs text-gray-500">{formatDate(session.created_at)}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteChat(session.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className={`flex-grow bg-white rounded-xl ${showSidebar ? 'rounded-l-none' : ''} shadow-md flex flex-col overflow-hidden`}>
        <motion.div 
          className="bg-expitrack-primary p-4 flex items-center justify-between"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Avatar className="h-10 w-10 bg-white border-2 border-white shadow-md">
                <AvatarFallback className="text-expitrack-primary font-bold">Lu</AvatarFallback>
                <AvatarImage src="/luna-avatar.png" alt="Luna AI" />
              </Avatar>
              <motion.span 
                className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-green-400 rounded-full border-2 border-white"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </div>
            <div>
              <h3 className="text-white font-semibold">Luna</h3>
              <p className="text-white/80 text-sm">Your ExpiTrack AI Assistant</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white/90 hover:text-white hover:bg-white/10 rounded-full"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <History className="h-5 w-5" />
          </Button>
        </motion.div>
        
        <ScrollArea className="flex-grow p-4 bg-gray-50">
          <div className="space-y-4 px-2">
            {messages.length === 0 && (
              <motion.div 
                className="text-center py-12"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div 
                  className="inline-block mb-4 p-3 bg-gradient-to-br from-expitrack-primary to-expitrack-primary/80 rounded-full shadow-lg"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center">
                    <Bot className="w-8 h-8 text-white" strokeWidth={1.5} />
                    <Sparkles className="w-5 h-5 text-yellow-300 absolute top-6 right-4" />
                  </div>
                </motion.div>
                <h3 className="text-lg font-medium mb-2 text-gray-800">How can I help you today?</h3>
              </motion.div>
            )}
            
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div 
                  key={index} 
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <motion.div 
                    className={`max-w-[85%] rounded-2xl p-4 shadow-sm ${
                      message.role === 'user' 
                        ? 'bg-expitrack-primary text-white rounded-br-none' 
                        : 'bg-white text-gray-800 rounded-bl-none border border-gray-100'
                    }`}
                    whileHover={{ scale: 1.01 }}
                    layout
                  >
                    <div className="flex justify-between items-start">
                      <div className="whitespace-pre-wrap break-words">
                        {message.content}
                      </div>
                      {message.role === 'assistant' && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 ml-3 text-gray-500 hover:text-expitrack-primary hover:bg-expitrack-primary/10 rounded-full" 
                          onClick={() => playTextToSpeech(message.content)}
                          disabled={isPlaying}
                        >
                          {isPlaying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume1 className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {isLoading && (
              <motion.div 
                className="flex justify-start"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.div 
                  className="bg-white rounded-2xl p-4 rounded-bl-none border border-gray-100 shadow-sm"
                  animate={{ 
                    boxShadow: ["0px 0px 0px rgba(0,168,107,0.2)", "0px 0px 8px rgba(0,168,107,0.5)", "0px 0px 0px rgba(0,168,107,0.2)"] 
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <div className="flex space-x-2">
                    <motion.div 
                      className="w-2 h-2 rounded-full bg-expitrack-primary"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                    />
                    <motion.div 
                      className="w-2 h-2 rounded-full bg-expitrack-primary"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                    />
                    <motion.div 
                      className="w-2 h-2 rounded-full bg-expitrack-primary"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>
        
        <motion.div 
          className="p-4 border-t border-gray-100 bg-white"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="flex items-end space-x-2">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                variant="outline"
                size="icon"
                onClick={() => document.getElementById('file-upload')?.click()}
                className="rounded-full h-10 w-10 border-gray-200 hover:border-expitrack-primary hover:bg-expitrack-primary/5"
              >
                <Upload className="h-5 w-5 text-expitrack-primary" />
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </Button>
            </motion.div>
            <div className="flex-grow relative">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Message Luna..."
                className="min-h-[50px] max-h-[150px] resize-none pr-12 rounded-full border-gray-200 focus:border-expitrack-primary focus:ring-1 focus:ring-expitrack-primary shadow-sm"
              />
              <motion.div 
                className="absolute bottom-1.5 right-1.5"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading}
                  className="rounded-full h-8 w-8 p-0 bg-expitrack-primary hover:bg-expitrack-primary/90 shadow-md"
                >
                  {isLoading ? 
                    <Loader2 className="h-4 w-4 animate-spin" /> : 
                    <Send className="h-4 w-4" />
                  }
                </Button>
              </motion.div>
            </div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                variant={isRecording ? "destructive" : "outline"}
                size="icon"
                onClick={isRecording ? stopRecording : startRecording}
                className={`rounded-full h-10 w-10 ${
                  isRecording ? 
                    "bg-red-500 hover:bg-red-600" : 
                    "border-gray-200 hover:border-expitrack-primary hover:bg-expitrack-primary/5"
                }`}
              >
                {isRecording ? 
                  <MicOff className="h-5 w-5" /> : 
                  <Mic className="h-5 w-5 text-expitrack-primary" />
                }
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LunaChat;
